import { useEffect, useRef } from 'react';

// IntersectionObserver-обёртка. При первом появлении элемента в видимой
// области (на 12 %) добавляем ему класс `reveal-on`, который запускает
// CSS-анимацию (fade/slide/zoom + stagger через --reveal-delay).
//
// Все анимации описаны в styles/index.css и срабатывают ТОЛЬКО на
// мобиле (≤ 768px) и при отсутствии `prefers-reduced-motion`.
//
// Использование:
//   const ref = useReveal();
//   <div ref={ref} data-reveal="up">…</div>
// или для списка с задержкой:
//   <div ref={ref} data-reveal="up" data-reveal-stagger>…</div>
export default function useReveal(options = {}) {
    const ref = useRef(null);
    const { rootMargin = '0px 0px -10% 0px', threshold = 0.12 } = options;

    useEffect(() => {
        const root = ref.current;
        if (!root) return;
        if (typeof window === 'undefined') return;

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const io = new IntersectionObserver(entries => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    e.target.classList.add('reveal-on');
                    io.unobserve(e.target);
                }
            });
        }, { rootMargin, threshold });

        // Сканер: находит все [data-reveal] и подписывает на observer.
        // Запускается на старте и при каждой мутации DOM (списки, грузящиеся
        // через API, начинают анимироваться без перезагрузки).
        const observed = new WeakSet();
        function scan() {
            const els = [root, ...root.querySelectorAll('[data-reveal]')];
            // Stagger — раздаём задержку детям
            root.querySelectorAll('[data-reveal-stagger]').forEach(box => {
                Array.from(box.children).forEach((child, k) => {
                    if (!child.hasAttribute('data-reveal')) {
                        child.setAttribute('data-reveal', 'up');
                    }
                    if (!child.style.getPropertyValue('--reveal-delay')) {
                        child.style.setProperty('--reveal-delay', `${k * 80}ms`);
                    }
                });
            });
            els.forEach(el => {
                if (!el.hasAttribute || !el.hasAttribute('data-reveal')) return;
                if (observed.has(el)) return;
                observed.add(el);
                if (reduce) {
                    el.classList.add('reveal-on');
                } else {
                    io.observe(el);
                }
            });
        }

        scan();
        const mo = new MutationObserver(scan);
        mo.observe(root, { childList: true, subtree: true });

        return () => { io.disconnect(); mo.disconnect(); };
    }, [rootMargin, threshold]);

    return ref;
}
